import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Titled card that holds one or more Trays (Key, Strings, ...). Rendered as a labelled
 * `role="group"` (not a landmark) so a screen reader announces the card name.
 * Source: boards 18 and 20 (paper card, 1px line border, 12px radius).
 *
 * @param {object} props
 * @param {string} props.title Card heading, also its accessible name.
 * @param {React.ReactNode} [props.hint] Right-aligned muted note, e.g. "Tap to mute".
 * @param {'sm'|'md'} [props.size] 'sm' = phone (board 18: p-2, gap 6, hint 12px, default; p-1.5 under 380px wide so
 *   7-column trays still get 44px options at 360px);
 *   'md' = desktop (board 20: p-3, gap 8, hint 13px).
 * @param {React.ReactNode} props.children
 * @param {string} [props.className]
 */
export function ControlCard({ title, hint, size = 'sm', children, className }) {
  return (
    <div
      role="group"
      aria-label={title}
      className={cn(
        'flex flex-col rounded-xl border border-line bg-paper',
        size === 'md' ? 'gap-2 p-3' : 'gap-1.5 p-2 max-[379px]:p-1.5',
        className,
      )}
    >
      <div className="flex items-baseline justify-between px-0.5">
        <span className="select-none text-[15px] font-extrabold leading-[18px] text-ink">{title}</span>
        {hint ? (
          <span className={cn('text-ink-muted', size === 'md' ? 'text-[13px]' : 'text-xs')}>{hint}</span>
        ) : null}
      </div>
      {children}
    </div>
  );
}

/**
 * Small sub-label above a Tray inside a card on desktop ("Mode", "Basic", "Extended").
 * Source: board 20 (13px, weight 700, muted, 2px side padding).
 */
export function TrayLabel({ children, className }) {
  return (
    <span className={cn('select-none px-0.5 text-[13px] font-bold text-ink-muted', className)}>
      {children}
    </span>
  );
}
